// src/app/@shared/services/print/print.service.ts
import { Injectable } from '@angular/core';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { PREPARING_PRINT_VIEW, CANCELING_PRINT_VIEW, CANCEL } from 'src/app/modules/route-planner/constants/multi-language.constants';

export interface PrintHeaderInfo {
  title?: string;
  details?: { label: string; value: string }[];
  dateTime?: {
    date?: string;
    time?: string;
    dateLabel?: string;
    timeLabel?: string;
  };
}

const CONTENT_WIDTH_PX = 1000;
const PAGE_WIDTH_MM = 210; // A4
const PAGE_HEIGHT_MM = 280;
const PAGE_MARGIN_MM = 10;

// Properties that hold nothing but a single color value - safe to replace with a normalized solid color.
// Anything else that matches UNSUPPORTED_COLOR_FUNCTION_PATTERN (background-image, mask, box-shadow, ...)
// is a shorthand/image-bearing property instead, so it gets cleared rather than rewritten.
const SIMPLE_COLOR_PROPERTIES = new Set([
  'color',
  'background-color',
  'border-top-color',
  'border-right-color',
  'border-bottom-color',
  'border-left-color',
  'outline-color',
  'text-decoration-color',
  'caret-color',
  'column-rule-color',
  'accent-color',
  'fill',
  'stroke',
  'stop-color',
  'flood-color',
  'lighting-color',
  '-webkit-text-fill-color',
  '-webkit-text-stroke-color',
]);

const UNSUPPORTED_COLOR_FUNCTION_PATTERN = /\b(?:color-mix|color|oklch|oklab|lch|lab|light-dark)\(/i;

const PRINT_CAPTURE_ROOT_CLASS = 'print-capture-root';
const SANITIZE_BATCH_SIZE = 150;

/**
 * Captures DOM elements and turns them into a printable PDF.
 * Each component decides *what* to capture and what header to show;
 * this service only handles the generic capture -> PDF -> output pipeline.
 *
 * html2canvas clones the *entire* live document (see its own DocumentCloner - it walks up to
 * ownerDocument.documentElement) to preserve stylesheet context, even though it only ever paints the
 * one element it's asked to capture. Left unchecked, that meant: (a) any unsupported CSS color function
 * anywhere on the whole page could crash the capture, and (b) every fixup pass here had to sweep the
 * whole page too, which was slow enough to feel like a hang. `ignoreElements` below tells html2canvas to
 * skip cloning anything outside printContainer entirely, so every fixup pass only needs to touch our own
 * small, disposable container - nothing else on the page is ever read or mutated, so there's nothing to
 * revert afterward either.
 */
@Injectable({
  providedIn: 'root',
})
export class PrintService {
  isPrinting = false;
  private loadingOverlay: HTMLElement | null = null;
  private colorNormalizationContext: CanvasRenderingContext2D | null = null;
  private cancelled = false;

  constructor(private readonly languageService: LanguageService) {}

  showLoadingIndicator(): void {
    if (this.loadingOverlay) {
      return;
    }
    this.isPrinting = true;
    this.loadingOverlay = this.buildLoadingOverlay();
    document.body.appendChild(this.loadingOverlay);
    void this.loadingOverlay.offsetHeight; // force layout now, so there's something ready to paint immediately
  }

  private hideLoadingIndicator(): void {
    if (this.loadingOverlay) {
      this.loadingOverlay.remove();
      this.loadingOverlay = null;
    }
    this.isPrinting = false;
  }

  async print(elements: HTMLElement[], header: PrintHeaderInfo = {}): Promise<void> {
    this.cancelled = false;
    this.showLoadingIndicator();
    await this.nextFrame(); // let the overlay paint before the heavy capture work blocks the thread

    const printContainer = this.buildPrintContainer(elements, header);
    document.body.appendChild(printContainer);

    this.neutralizePseudoElementStyles(printContainer);
    await this.sanitizeColors(printContainer);
    this.ensureReadableTextColor(printContainer);
    this.convertGridsToFlex(printContainer);
    this.forceExpandPanels(printContainer);
    this.expandScrollableContainers(printContainer);
    // Measured now, right before capture, while printContainer's layout still matches exactly what
    // html2canvas is about to render (scale: 1, so DOM px map 1:1 to canvas px).
    const unsafeBreakRanges = this.collectUnsafeBreakRanges(printContainer);

    // html/body are real, persistent elements (not part of our disposable printContainer) that
    // ignoreElements still lets html2canvas traverse, since it has to pass through them to ever reach
    // printContainer at all - they've never been sanitized before, so they - and their own
    // ::before/::after - need their own (reverted-afterward) fixup pass.
    const restoreAncestorColors = this.sanitizeAncestorColors();
    const restoreAncestorPseudoElements = this.neutralizeAncestorPseudoElementStyles();

    try {
      if (this.cancelled) {
        return;
      }

      const canvas = await html2canvas(printContainer, {
        useCORS: true,
        scale: 1,
        logging: false,
        backgroundColor: '#FFFFFF',
        // See the class-level doc comment: skip everything outside our own container entirely. Must
        // still let html2canvas reach printContainer's own ancestors (html/body) - containment only
        // checks descendants, so "printContainer contains body" is false even though body is exactly
        // the ancestor the traversal needs to pass through to ever reach printContainer at all. <head>
        // (stylesheets/fonts) is kept too, or the capture would render with no CSS classes applied.
        ignoreElements: (element) =>
          !document.head.contains(element) && !element.contains(printContainer) && !printContainer.contains(element),
        // html2canvas replaces every hyphenated tag (mat-expansion-panel, mat-expansion-panel-header,
        // any app-* component, ...) with a generic element carrying no class attribute at all, which
        // breaks ancestor-class-dependent CSS rules like `.mat-expansion-panel.mat-expanded > ...`.
        // forceExpandPanels() above sets classes on the *original* tree for defense in depth, but the
        // guaranteed fix operates here, on html2canvas's own already-cloned document, directly on the
        // plain <div>s Material's collapse mechanism actually hides (those keep their classes).
        onclone: (clonedDocument) => this.forceExpandPanelsInClone(clonedDocument),
      });

      // html2canvas itself can't be aborted mid-flight, but we can at least skip the save if the
      // user cancelled while it was running instead of forcing a download they no longer asked for.
      if (!this.cancelled) {
        const pdf = await this.buildPdfFromCanvas(canvas, unsafeBreakRanges);
        if (!this.cancelled) {
          pdf.save(`route-details-${this.formatDateForFilename(new Date())}.pdf`);
        }
      }
    } catch (error) {
      console.error('[PrintService] Failed to generate print view:', error);
    } finally {
      restoreAncestorColors();
      restoreAncestorPseudoElements();
      printContainer.remove();
      this.hideLoadingIndicator();
    }
  }

  /**
   * A single requestAnimationFrame callback still resolves its continuation as a microtask *before*
   * the browser paints that frame, so waiting on just one rAF is not enough to guarantee the loading
   * popup is actually visible yet. Following it with a macrotask (setTimeout) reliably lands after
   * the browser has had a chance to paint.
   */
  private nextFrame(): Promise<void> {
    return new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
  }

  /**
   * ::before/::after pseudo-elements (Material's M3 state-layer/ripple overlays are built from these,
   * typically via color-mix()) have no inline style to override - they only exist through the cascade.
   * Disable their visuals with an injected stylesheet rule instead; they add nothing to a print capture.
   * This also has to force every *color-bearing* pseudo-element property to a safe value (not just
   * background) - html2canvas explicitly reads pseudo-element computed style
   * (getComputedStyle(node, ':before')), and sanitizeColors can never reach pseudo-elements itself since
   * they aren't real DOM nodes reachable via querySelectorAll. `inherit` falls back to the real host
   * element's own color, which sanitizeColors *does* cover.
   *
   * Also kills every transition/animation within the capture. Material's expansion panel animates exactly
   * the properties forceExpandPanels forces open (grid-template-rows, height, visibility) - if html2canvas
   * happens to snapshot mid-transition, the panel can measure as fully open (final layout size already
   * committed) while still being mid-way through painting, which looks identical to "correctly sized but
   * empty". Instant, final values everywhere removes that class of bug entirely.
   *
   * printContainer is a throwaway element we built ourselves and always remove after capture, so there's
   * nothing here to revert afterward.
   */
  private neutralizePseudoElementStyles(root: HTMLElement): void {
    root.classList.add(PRINT_CAPTURE_ROOT_CLASS);

    const style = document.createElement('style');
    style.textContent = `
      .${PRINT_CAPTURE_ROOT_CLASS} *::before, .${PRINT_CAPTURE_ROOT_CLASS} *::after {
        background: none !important;
        box-shadow: none !important;
        filter: none !important;
        color: inherit !important;
        border-color: inherit !important;
        outline-color: inherit !important;
        text-decoration-color: inherit !important;
        caret-color: inherit !important;
      }
      .${PRINT_CAPTURE_ROOT_CLASS}, .${PRINT_CAPTURE_ROOT_CLASS} * {
        transition: none !important;
        animation: none !important;
      }
    `;
    root.prepend(style);
  }

  /**
   * html/body's own ::before/::after (a global app-shell effect, say) can't be reached by
   * neutralizePseudoElementStyles above (that's scoped to printContainer's descendants only) - and
   * they're real, persistent elements, not part of our disposable printContainer, so this - unlike
   * everything scoped to printContainer - has to be explicitly reverted afterward.
   */
  private neutralizeAncestorPseudoElementStyles(): () => void {
    const style = document.createElement('style');
    style.textContent = `
      html::before, html::after, body::before, body::after {
        background: none !important;
        box-shadow: none !important;
        filter: none !important;
        color: inherit !important;
        border-color: inherit !important;
        outline-color: inherit !important;
        text-decoration-color: inherit !important;
        caret-color: inherit !important;
      }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }

  /**
   * document.documentElement/document.body themselves are never covered by sanitizeColors (which only
   * scans printContainer's own subtree) - but ignoreElements (see print()) lets html2canvas's traversal
   * pass through them regardless, since it has to in order to ever reach printContainer. If either has
   * global styling using an unsupported color function, this is the only thing that would catch it.
   * Real, persistent elements - has to be reverted afterward, unlike printContainer's own disposable copy.
   */
  private sanitizeAncestorColors(): () => void {
    const ctx = this.getColorNormalizationContext();
    const restoreFns: (() => void)[] = [];

    [document.documentElement, document.body].forEach((element) => {
      const computed = window.getComputedStyle(element);
      for (let i = 0; i < computed.length; i++) {
        const property = computed.item(i);
        const value = computed.getPropertyValue(property);
        if (!value || !UNSUPPORTED_COLOR_FUNCTION_PATTERN.test(value)) {
          continue;
        }

        const previousValue = element.style.getPropertyValue(property);
        const previousPriority = element.style.getPropertyPriority(property);
        restoreFns.push(() => {
          if (previousValue) {
            element.style.setProperty(property, previousValue, previousPriority);
          } else {
            element.style.removeProperty(property);
          }
        });

        if (this.isColorValuedProperty(property)) {
          element.style.setProperty(property, this.normalizeColor(ctx, value), 'important');
        } else {
          element.style.setProperty(property, 'none', 'important');
        }
      }
    });

    return () => restoreFns.forEach((restore) => restore());
  }

  /**
   * html2canvas only understands rgb()/hsl(). Modern CSS (Material's M3 theme, color-mix, oklch, ...)
   * can resolve computed colors to the CSS Color-4 color() function, which makes it throw. Re-resolve
   * every color-ish property through a canvas 2d context, which always normalizes any valid CSS color
   * (whatever function it was written in) down to rgb()/rgba().
   *
   * Split into a read pass (getComputedStyle - forces a synchronous layout recalc) and a separate write
   * pass (style.setProperty - doesn't need layout). Interleaving the two per element instead would force
   * a fresh layout recalculation on every single element rather than once for the whole batch.
   *
   * printContainer is a throwaway element we built ourselves and always remove after capture, so there's
   * nothing here to revert afterward.
   */
  private async sanitizeColors(root: HTMLElement): Promise<void> {
    const ctx = this.getColorNormalizationContext();
    const elements = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))];
    const pendingChanges: { element: HTMLElement; property: string; value: string }[] = [];

    for (let start = 0; start < elements.length; start += SANITIZE_BATCH_SIZE) {
      if (this.cancelled) {
        break;
      }

      elements.slice(start, start + SANITIZE_BATCH_SIZE).forEach((element) => {
        // Check every computed property, not a curated subset - a curated list previously missed
        // properties this app actually uses (e.g. RTL logical properties like border-inline-*-color)
        // and let the crash back in.
        const computed = window.getComputedStyle(element);
        for (let i = 0; i < computed.length; i++) {
          const property = computed.item(i);
          const value = computed.getPropertyValue(property);
          if (value && UNSUPPORTED_COLOR_FUNCTION_PATTERN.test(value)) {
            pendingChanges.push({ element, property, value });
          }
        }
      });

      if (start + SANITIZE_BATCH_SIZE < elements.length) {
        await this.yieldToMain();
      }
    }

    pendingChanges.forEach(({ element, property, value }) => {
      if (this.isColorValuedProperty(property)) {
        element.style.setProperty(property, this.normalizeColor(ctx, value), 'important');
      } else {
        // Shorthand/image-bearing property (background-image, mask, box-shadow, ...) - not worth
        // reconstructing for a print capture, just drop it.
        element.style.setProperty(property, 'none', 'important');
      }
    });
  }

  /**
   * True for real CSS properties known to hold nothing but a single color, plus any custom property
   * (--mat-*-color, etc.) - Material's M3 theme defines its tokens by name convention rather than as
   * real typed CSS properties, but they're overwhelmingly color values in practice (confirmed by their
   * own "-color"-suffixed names), so they get a real normalized color rather than defaulting to 'none'.
   */
  private isColorValuedProperty(property: string): boolean {
    return SIMPLE_COLOR_PROPERTIES.has(property) || property.startsWith('--');
  }

  private yieldToMain(): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, 0));
  }

  /**
   * Some shared components hardcode near-white text (with !important) because they're normally only
   * ever shown on a dark background. That's invisible once captured onto our plain white print page,
   * regardless of whether the color came from the cascade or a hardcoded !important rule - an inline
   * !important override here beats any external stylesheet rule, important or not.
   */
  private ensureReadableTextColor(root: HTMLElement): void {
    const elements = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))];
    // Read pass first (getComputedStyle forces a layout recalc), then write - interleaving the two
    // per element would force a fresh recalc on every single one instead of once for the whole pass.
    const toDarken = elements.filter((element) => this.isNearWhite(window.getComputedStyle(element).color));
    toDarken.forEach((element) => element.style.setProperty('color', '#1a1a1a', 'important'));
  }

  /**
   * html2canvas has weak/no support for CSS Grid (the same limitation that broke mat-expansion-panel's
   * collapse mechanism). Any *other* component's grid-based layout is liable to hit the same wall, so
   * proactively replace every display:grid with an equivalent flex-wrap - not pixel-identical to the
   * original grid, but keeps content visible and readable rather than silently mispositioned/clipped.
   */
  private convertGridsToFlex(root: HTMLElement): void {
    const elements = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))];
    const gridElements = elements.filter((element) => {
      const display = window.getComputedStyle(element).display;
      return display === 'grid' || display === 'inline-grid';
    });
    gridElements.forEach((element) => {
      element.style.setProperty('display', 'flex', 'important');
      element.style.setProperty('flex-wrap', 'wrap', 'important');
    });
  }

  /**
   * A nested list/panel with its own overflow-y: auto and a fixed/max height only shows html2canvas
   * whatever fits in that inner scroll viewport - anything below it is never painted onto the canvas at
   * all (not even split across pages; page-slicing only operates on the already-rendered result, so it
   * can't recover content that was never rendered in the first place). Force every such container to its
   * full natural height so nothing is clipped before the capture even happens.
   */
  private expandScrollableContainers(root: HTMLElement): void {
    const elements = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))];
    const scrollableElements = elements.filter((element) => {
      const overflowY = window.getComputedStyle(element).overflowY;
      return overflowY === 'auto' || overflowY === 'scroll';
    });
    scrollableElements.forEach((element) => {
      element.style.setProperty('overflow-y', 'visible', 'important');
      element.style.setProperty('max-height', 'none', 'important');
      element.style.setProperty('height', 'auto', 'important');
    });
  }

  /**
   * A tall capture gets sliced into PDF pages at fixed pixel intervals (see addSlicedPages), with no
   * awareness of where content actually sits - a text row landing exactly on a cut line gets its text
   * sliced apart between two pages, which reads as "missing" rather than "split". Record every leaf
   * element's vertical span (relative to root) so the slicer can nudge a page break above one instead
   * of through it. Measured right before capture, so these coordinates match the canvas 1:1 (scale: 1).
   */
  private collectUnsafeBreakRanges(root: HTMLElement): { top: number; bottom: number }[] {
    const rootTop = root.getBoundingClientRect().top;
    return Array.from(root.querySelectorAll<HTMLElement>('*'))
      .filter((element) => element.children.length === 0 && !!element.textContent?.trim())
      .map((element) => {
        const rect = element.getBoundingClientRect();
        return { top: rect.top - rootTop, bottom: rect.bottom - rootTop };
      });
  }

  /**
   * Angular Material's mat-expansion-panel keeps its collapsed body in the DOM and hides it purely
   * via CSS (driven by a `.mat-expanded` class on the panel host) - so a plain clone captures whatever
   * expand/collapse state it happened to be in live. Force every panel open for print, on the original
   * tree - defense in depth; the fix that actually matters is forceExpandPanelsInClone below.
   */
  private forceExpandPanels(root: HTMLElement): void {
    root.querySelectorAll('mat-expansion-panel').forEach((panel) => {
      panel.classList.add('mat-expanded');
      const header = panel.querySelector(':scope > mat-expansion-panel-header');
      if (header instanceof HTMLElement) {
        header.classList.add('mat-expanded');
        header.setAttribute('aria-expanded', 'true');
      }
    });

    this.openExpansionPanelBodies(root);
  }

  /**
   * html2canvas replaces every hyphenated tag (mat-expansion-panel included) with a classless generic
   * element, which breaks Material's `.mat-expansion-panel.mat-expanded > ...` collapse CSS regardless
   * of the `.mat-expanded` class forceExpandPanels adds above - that class ends up on an element that no
   * longer exists in html2canvas's own rendered tree. The wrapper/content divs are plain <div>s though,
   * so they keep their classes - force them open directly here, on html2canvas's already-cloned
   * document (its `onclone` hook), bypassing the broken ancestor selector entirely.
   */
  private forceExpandPanelsInClone(clonedDocument: Document): void {
    this.openExpansionPanelBodies(clonedDocument);
  }

  private openExpansionPanelBodies(root: ParentNode): void {
    root.querySelectorAll('.mat-expansion-panel-content-wrapper').forEach((wrapper) => {
      if (!(wrapper instanceof HTMLElement)) {
        return;
      }
      wrapper.removeAttribute('inert');
      wrapper.style.setProperty('display', 'block', 'important');
      wrapper.style.setProperty('height', 'auto', 'important');
      wrapper.style.setProperty('grid-template-rows', 'unset', 'important');

      const content = wrapper.querySelector(':scope > .mat-expansion-panel-content');
      if (content instanceof HTMLElement) {
        content.style.setProperty('visibility', 'visible', 'important');
        content.style.setProperty('height', 'auto', 'important');
        content.style.setProperty('display', 'flex', 'important');
      }
    });
  }

  private isNearWhite(rgbColor: string): boolean {
    const match = rgbColor.match(/rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/);
    if (!match) {
      return false;
    }
    const [r, g, b] = match.slice(1, 4).map(Number);
    return r > 235 && g > 235 && b > 235;
  }

  /**
   * Reading back ctx.fillStyle as a *string* isn't reliable - Chrome serializes some alpha-inclusive
   * colors (particularly ones resolved from color-mix()) back out using the CSS Color-4 color()
   * function itself, i.e. exactly the syntax html2canvas can't parse, defeating the whole point. Reading
   * the raw pixel bytes back via getImageData and building the rgba() string ourselves sidesteps the
   * browser's own (unpredictable) color serialization entirely - integers in, a fixed legacy format out.
   */
  private normalizeColor(ctx: CanvasRenderingContext2D, cssColor: string): string {
    try {
      ctx.clearRect(0, 0, 1, 1);
      ctx.fillStyle = '#000000';
      ctx.fillStyle = cssColor;
      ctx.fillRect(0, 0, 1, 1);
      const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
      return `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(3)})`;
    } catch {
      return cssColor;
    }
  }

  private getColorNormalizationContext(): CanvasRenderingContext2D {
    if (!this.colorNormalizationContext) {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      // willReadFrequently: normalizeColor calls getImageData on this same 1x1 context repeatedly
      // (once per matched color across the whole capture), which this hint avoids re-syncing for.
      this.colorNormalizationContext = canvas.getContext('2d', { willReadFrequently: true })!;
    }
    return this.colorNormalizationContext;
  }

  private buildPrintContainer(elements: HTMLElement[], header: PrintHeaderInfo): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = `
      position: absolute; left: -9999px; top: 0;
      width: ${CONTENT_WIDTH_PX}px; background-color: white; color: #1a1a1a; padding: 20px;
    `;
    // Captured elements are cloned out of their normal (often dark-background) context, where they may
    // rely on inherited light text color - force a readable default here; elements with their own
    // explicit color class still win via specificity.

    container.appendChild(this.buildHeaderElement(header));

    elements.forEach((element) => {
      const clone = element.cloneNode(true) as HTMLElement;
      this.stripInteractiveElements(clone);
      container.appendChild(clone);
    });

    return container;
  }

  private stripInteractiveElements(root: HTMLElement): void {
    // Only real controls - NOT [role="button"], which this app also uses on large content
    // wrappers (e.g. a whole result card) purely for click/keyboard handling.
    root.querySelectorAll('button, mot-button, mot-menu, input').forEach((element) => element.remove());
  }

  private buildHeaderElement(header: PrintHeaderInfo): HTMLElement {
    const wrapper = document.createElement('div');

    if (header.title) {
      const title = document.createElement('h2');
      title.textContent = header.title;
      wrapper.appendChild(title);
    }

    if (header.dateTime?.date || header.dateTime?.time) {
      const dateTime = document.createElement('div');
      dateTime.style.cssText = 'margin-bottom: 10px; font-weight: bold;';
      if (header.dateTime.date) {
        dateTime.append(`${header.dateTime.dateLabel ?? 'Date'}: ${header.dateTime.date}  `);
      }
      if (header.dateTime.time) {
        dateTime.append(`${header.dateTime.timeLabel ?? 'Time'}: ${header.dateTime.time}`);
      }
      wrapper.appendChild(dateTime);
    }

    if (header.details?.length) {
      const details = document.createElement('div');
      details.style.cssText = 'display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px;';
      header.details.forEach(({ label, value }) => {
        const item = document.createElement('div');
        const strong = document.createElement('strong');
        strong.textContent = `${label}: `;
        item.append(strong, value);
        details.appendChild(item);
      });
      wrapper.appendChild(details);
    }

    const hr = document.createElement('hr');
    hr.style.margin = '15px 0';
    wrapper.appendChild(hr);

    return wrapper;
  }

  private async buildPdfFromCanvas(
    canvas: HTMLCanvasElement,
    unsafeBreakRanges: { top: number; bottom: number }[]
  ): Promise<jsPDF> {
    const pdf = new jsPDF('p', 'mm', 'a4', true);
    const usableHeight = PAGE_HEIGHT_MM - PAGE_MARGIN_MM * 2;
    const imgHeight = (canvas.height * PAGE_WIDTH_MM) / canvas.width;

    if (imgHeight <= usableHeight) {
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, PAGE_MARGIN_MM, PAGE_WIDTH_MM, imgHeight);
    } else {
      await this.addSlicedPages(pdf, canvas, usableHeight, unsafeBreakRanges);
    }

    this.addPageNumbers(pdf);
    return pdf;
  }

  /**
   * Splits an over-tall capture into multiple A4 pages. Page breaks default to fixed-height slices, but
   * get nudged up to just above any leaf element (a text row, a single line) they'd otherwise cut through
   * - see collectUnsafeBreakRanges for why that matters. Yields between pages so a long document doesn't
   * block the main thread (and the popup's cancel button) in one long stretch.
   */
  private async addSlicedPages(
    pdf: jsPDF,
    canvas: HTMLCanvasElement,
    usableHeight: number,
    unsafeBreakRanges: { top: number; bottom: number }[]
  ): Promise<void> {
    const pxPerMm = canvas.width / PAGE_WIDTH_MM;
    const usableHeightPx = usableHeight * pxPerMm;

    let sourceY = 0;
    let pageIndex = 0;

    while (sourceY < canvas.height && !this.cancelled) {
      if (pageIndex > 0) {
        pdf.addPage();
      }

      const naiveCutY = Math.min(sourceY + usableHeightPx, canvas.height);
      const cutY = naiveCutY >= canvas.height ? naiveCutY : this.avoidSplitting(naiveCutY, sourceY, unsafeBreakRanges);
      // Math.max guards against Math.round ever landing on sourceY itself (e.g. a nudge target of
      // sourceY + 0.3 rounds down to sourceY) - without it, sourceY could fail to advance and the
      // while loop would spin forever.
      const sourceHeight = Math.max(1, Math.round(cutY) - sourceY);

      const slice = document.createElement('canvas');
      slice.width = canvas.width;
      slice.height = sourceHeight;
      const ctx = slice.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, slice.width, slice.height);
      ctx.drawImage(canvas, 0, sourceY, canvas.width, sourceHeight, 0, 0, slice.width, slice.height);

      pdf.addImage(slice.toDataURL('image/jpeg', 0.95), 'JPEG', 0, PAGE_MARGIN_MM, PAGE_WIDTH_MM, sourceHeight / pxPerMm);

      sourceY += sourceHeight;
      pageIndex++;

      if (sourceY < canvas.height) {
        await this.yieldToMain();
      }
    }
  }

  private avoidSplitting(naiveCutY: number, pageStartY: number, unsafeBreakRanges: { top: number; bottom: number }[]): number {
    // Only worth nudging the break if doing so still leaves a reasonable amount of content on this page -
    // otherwise a leaf sitting right at the naive cut would force a near-empty page. MIN_REMAINING_PX is
    // an arbitrary "roughly one text line" threshold, not tied to any specific element's actual height.
    const MIN_REMAINING_PX = 20;

    const adjustedCutY = unsafeBreakRanges.reduce(
      (cutY, range) => (naiveCutY > range.top && naiveCutY < range.bottom ? Math.min(cutY, range.top) : cutY),
      naiveCutY
    );

    return adjustedCutY - pageStartY >= MIN_REMAINING_PX ? adjustedCutY : naiveCutY;
  }

  private addPageNumbers(pdf: jsPDF): void {
    const totalPages = pdf.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      const text = `${i} / ${totalPages}`;
      const textWidth = (pdf.getStringUnitWidth(text) * 10) / pdf.internal.scaleFactor;
      pdf.text(text, (PAGE_WIDTH_MM - textWidth) / 2, PAGE_HEIGHT_MM - 5);
    }
  }

  private formatDateForFilename(date: Date): string {
    const pad = (value: number) => value.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  private buildLoadingOverlay(): HTMLElement {
    this.ensureSpinnerKeyframes();
    const currentLanguage = this.languageService.getSelectedLanguage().language;

    const overlay = document.createElement('div');
    overlay.id = 'print-loading-overlay';
    overlay.style.cssText = `
      position: fixed; inset: 0; background-color: rgba(0, 0, 0, 0.7); z-index: 99999;
      display: flex; flex-direction: column; justify-content: center; align-items: center;
    `;

    const popup = document.createElement('div');
    popup.style.cssText = `
      background-color: #ffffff; border-radius: 8px; padding: 30px; max-width: 400px;
      width: 90%; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3); text-align: center;
    `;

    const spinner = document.createElement('div');
    spinner.style.cssText = `
      width: 50px; height: 50px; margin: 0 auto 20px; border: 4px solid #f3f3f3;
      border-top: 4px solid #0068F5; border-radius: 50%; animation: printSpin 1s linear infinite;
    `;

    const text = document.createElement('div');
    text.style.cssText = 'margin-bottom: 30px; font-size: 16px; color: #333333; line-height: 1.5;';
    text.textContent = PREPARING_PRINT_VIEW[currentLanguage];

    const cancelButton = document.createElement('button');
    cancelButton.textContent = CANCEL[currentLanguage];
    cancelButton.style.cssText = `
      background-color: #f44336; color: #ffffff; border: none; border-radius: 4px;
      padding: 12px 24px; font-size: 14px; cursor: pointer !important; font-weight: 500; min-width: 120px;
    `;
    cancelButton.onmouseenter = () => (cancelButton.style.backgroundColor = '#d32f2f');
    cancelButton.onmouseleave = () => (cancelButton.style.backgroundColor = '#f44336');
    cancelButton.onclick = () => {
      this.cancelled = true;
      // Give visible confirmation the click registered, rather than the popup vanishing abruptly.
      // The popup itself is closed by print()'s own finally block once the in-flight pipeline
      // actually notices `cancelled` (html2canvas can't be aborted mid-capture, so that may take
      // a moment - see the "Cancelling..." text above).
      text.textContent = CANCELING_PRINT_VIEW[currentLanguage];
      cancelButton.disabled = true;
      cancelButton.style.cursor = 'default';
      cancelButton.style.opacity = '0.6';
    };

    popup.append(spinner, text, cancelButton);
    overlay.appendChild(popup);
    return overlay;
  }

  private ensureSpinnerKeyframes(): void {
    if (document.getElementById('print-spinner-style')) {
      return;
    }
    const style = document.createElement('style');
    style.id = 'print-spinner-style';
    style.textContent = '@keyframes printSpin { to { transform: rotate(360deg); } }';
    document.head.appendChild(style);
  }
}
